// Part of salesPages, split by screen to keep files short; ../salesPages.ts merges the parts.
// Keys sit directly under salesPages, in sub-objects whose names no other part uses.
export const en = {
  // Shared by the sales lists (all sales, overview, invoices, quotes).
  salesListCommon: {
    newTransaction: 'New transaction',
    newInvoice: 'New invoice',
    newQuote: 'New quote',
    noSalesYet: 'No sales yet',
    statusLabel: 'Status',
    dateLabel: 'Date',
  },
  // Sales › All sales (routes/sales/all.tsx).
  allSales: {
    title: 'All sales',
    typeLabel: 'Type',
    types: {
      all: 'All transactions',
      invoice: 'Invoices',
      quote: 'Quotes',
      credit_note: 'Credit notes',
      sales_receipt: 'Sales receipts',
      payment: 'Payments',
    },
    allStatuses: 'All statuses',
    paidClosed: 'Paid / closed',
    customerLabel: 'Customer',
    allCustomers: 'All customers',
    searchPlaceholder: 'Search no., customer or memo',
    searchLabel: 'Search sales',
    loadError: 'Could not load sales',
    noMatch: 'No transactions match these filters',
  },
  // Sales › Overview (routes/sales/index.tsx).
  salesHome: {
    title: 'Sales',
    description: 'Overview',
    shortcuts: 'Shortcuts',
    receivePayment: 'Receive payment',
    newCustomer: 'New customer',
    recentSales: 'Recent sales',
    seeAllSales: 'See all sales',
    emptyStaff: 'Create an invoice, quote or sales receipt to see it here.',
    emptyViewer: 'Invoices and payments will appear here.',
  },
  // Sales › Invoices (routes/sales/invoices/index.tsx).
  invoiceList: {
    title: 'Invoices',
    notDue: 'Not due yet',
    searchPlaceholder: 'Search by customer, invoice no. or memo',
    searchLabel: 'Search invoices',
    einvoiceLabel: 'E-invoice',
    einvoiceFilter: 'E-invoice: {{filter}}',
    loadError: 'Could not load invoices',
    noMatch: 'No invoices match these filters',
    empty: 'No invoices yet',
  },
  // Sales › Quotes (routes/sales/quotes/index.tsx).
  quoteList: {
    title: 'Quotes',
    searchPlaceholder: 'Search by customer or quote no.',
    searchLabel: 'Search quotes',
    noMatch: 'No quotes match these filters',
  },
  // A customer payment, read-only (routes/sales/payments/$paymentId.tsx).
  paymentView: {
    title: 'Payment {{number}}',
    voided: 'Payment {{number}} voided.',
    notFound: 'Payment not found',
    transactionJournal: 'Transaction journal',
    voidNotice:
      'This payment is void. Its posting was reversed and the invoices it paid are open again.',
    voidNoticeReason:
      'This payment is void: {{reason}}. Its posting was reversed and the invoices it paid are open again.',
    customer: 'Customer',
    paymentDate: 'Payment date',
    paymentMethod: 'Payment method',
    referenceNo: 'Reference no.',
    depositTo: 'Deposit to',
    amountReceived: 'Amount received',
    exchangeRate: 'Exchange rate',
    amountReceivedIn: 'Amount received in {{currency}}',
    unapplied: 'Unapplied (credit on account)',
    memo: 'Memo',
    appliedTo: 'Applied to',
    journalEntry: 'Journal entry {{number}}',
    invoice: 'Invoice {{number}}',
    exchangeGain: 'Exchange gain {{amount}}',
    exchangeLoss: 'Exchange loss {{amount}}',
    totalApplied: 'Total applied',
    notApplied: 'Not applied to any invoice.',
    voidTitle: 'Void payment {{number}}',
    voidBody:
      'Voiding posts a reversing entry and reopens the invoices this payment settled. This cannot be undone.',
  },
  // Create statements (routes/sales/statements/new.tsx).
  statementNew: {
    title: 'Create statements',
    enterDate: 'Enter the statement date.',
    tickCustomer: 'Tick at least one customer.',
    startAfterDate: 'The start date must be on or before the statement date.',
    sent_one: '{{count}} statement sent.',
    sent_other: '{{count}} statements sent.',
    sentSkipped_one: '{{count}} statement sent, {{skipped}} skipped.',
    sentSkipped_other: '{{count}} statements sent, {{skipped}} skipped.',
    printPreview: 'Print or preview',
    sending: 'Sending…',
    saveAndSend: 'Save and send',
    sentTo: 'Sent to {{list}}.',
    sentToRecorded:
      'Sent to {{list}}. This server keeps them on Sent emails and does not deliver them.',
    recipient: '{{name}} ({{email}})',
    listSeparator: ', ',
    statementType: 'Statement type',
    types: {
      balance_forward: 'Balance forward',
      open_item: 'Open item (invoices still open)',
      transaction: 'Transaction statement',
    },
    statementDate: 'Statement date',
    startDate: 'Start date',
    endDate: 'End date',
    customers: 'Customers',
    withBalance: 'With a balance',
    withOverdue: 'With an overdue balance',
    allCustomers: 'All customers',
    explainBalanceForward:
      'The balance at {{from}}, then every invoice, payment and credit to {{to}} with a running balance.',
    explainOpenItem: 'The invoices still open at {{to}}, with unused credits.',
    explainTransaction: 'Every transaction from {{from}} to {{to}}.',
    noCustomers: 'No customers to show',
    noCustomersHint: 'Choose All customers to see every customer.',
    selectAll: 'Select all customers',
    colCustomer: 'Customer',
    colEmail: 'Email',
    colOverdue: 'Overdue',
    colBalance: 'Balance',
    include: 'Include {{name}}',
    noEmail: 'No email: print instead',
    selected_one: '{{count}} selected',
    selected_other: '{{count}} selected',
    total: 'Total {{amount}}',
  },
  // Send a sales document by email (components/sales/send-dialog.tsx).
  sendDialog: {
    title: 'Send {{what}}',
    separate: 'Separate several addresses with commas.',
    separateRecorded:
      'Separate several addresses with commas. This server records the email on Sent emails instead of delivering it.',
    to: 'To *',
    cc: 'Cc',
    language: 'Language',
    languageHint: 'Of the message and the attached document.',
    arabic: 'Arabic',
    english: 'English',
    arabicUnavailable:
      'This customer gets documents in Arabic, but this server prints in English only for now.',
    subject: 'Subject *',
    message: 'Message',
    sending: 'Sending…',
    send: 'Send',
    failed: '{{what}} could not be delivered: {{error}}.',
    unknownError: 'unknown error',
    recorded: '{{what}} recorded for {{to}}. This server does not deliver email.',
    sent: '{{what}} sent to {{to}}.',
  },
};

export const ar = {
  salesListCommon: {
    newTransaction: 'معاملة جديدة',
    newInvoice: 'فاتورة جديدة',
    newQuote: 'عرض سعر جديد',
    noSalesYet: 'لا توجد مبيعات بعد',
    statusLabel: 'الحالة',
    dateLabel: 'التاريخ',
  },
  allSales: {
    title: 'كل المبيعات',
    typeLabel: 'النوع',
    types: {
      all: 'كل المعاملات',
      invoice: 'الفواتير',
      quote: 'عروض الأسعار',
      credit_note: 'الإشعارات الدائنة',
      sales_receipt: 'إيصالات المبيعات',
      payment: 'المقبوضات',
    },
    allStatuses: 'كل الحالات',
    paidClosed: 'مدفوعة / مغلقة',
    customerLabel: 'العميل',
    allCustomers: 'كل العملاء',
    searchPlaceholder: 'ابحث بالرقم أو العميل أو المذكرة',
    searchLabel: 'البحث في المبيعات',
    loadError: 'تعذّر تحميل المبيعات',
    noMatch: 'لا توجد معاملات تطابق عوامل التصفية هذه',
  },
  salesHome: {
    title: 'المبيعات',
    description: 'نظرة عامة',
    shortcuts: 'اختصارات',
    receivePayment: 'استلام دفعة',
    newCustomer: 'عميل جديد',
    recentSales: 'أحدث المبيعات',
    seeAllSales: 'عرض كل المبيعات',
    emptyStaff: 'أنشئ فاتورة أو عرض سعر أو إيصال مبيعات ليظهر هنا.',
    emptyViewer: 'ستظهر الفواتير والمقبوضات هنا.',
  },
  invoiceList: {
    title: 'الفواتير',
    notDue: 'غير مستحقة بعد',
    searchPlaceholder: 'ابحث بالعميل أو رقم الفاتورة أو المذكرة',
    searchLabel: 'البحث في الفواتير',
    einvoiceLabel: 'الفاتورة الإلكترونية',
    einvoiceFilter: 'الفاتورة الإلكترونية: {{filter}}',
    loadError: 'تعذّر تحميل الفواتير',
    noMatch: 'لا توجد فواتير تطابق عوامل التصفية هذه',
    empty: 'لا توجد فواتير بعد',
  },
  quoteList: {
    title: 'عروض الأسعار',
    searchPlaceholder: 'ابحث بالعميل أو رقم عرض السعر',
    searchLabel: 'البحث في عروض الأسعار',
    noMatch: 'لا توجد عروض أسعار تطابق عوامل التصفية هذه',
  },
  paymentView: {
    title: 'سند قبض {{number}}',
    voided: 'أُلغي سند القبض {{number}}.',
    notFound: 'لم يُعثر على سند القبض',
    transactionJournal: 'قيد المعاملة',
    voidNotice: 'سند القبض هذا ملغى. عُكس قيده وأصبحت الفواتير التي سدّدها مفتوحة من جديد.',
    voidNoticeReason:
      'سند القبض هذا ملغى: {{reason}}. عُكس قيده وأصبحت الفواتير التي سدّدها مفتوحة من جديد.',
    customer: 'العميل',
    paymentDate: 'تاريخ الدفعة',
    paymentMethod: 'طريقة الدفع',
    referenceNo: 'الرقم المرجعي',
    depositTo: 'الإيداع في',
    amountReceived: 'المبلغ المستلم',
    exchangeRate: 'سعر الصرف',
    amountReceivedIn: 'المبلغ المستلم بعملة {{currency}}',
    unapplied: 'غير مخصص (رصيد دائن في الحساب)',
    memo: 'مذكرة',
    appliedTo: 'مخصص إلى',
    journalEntry: 'قيد يومية {{number}}',
    invoice: 'فاتورة {{number}}',
    exchangeGain: 'ربح صرف {{amount}}',
    exchangeLoss: 'خسارة صرف {{amount}}',
    totalApplied: 'إجمالي المخصص',
    notApplied: 'غير مخصص لأي فاتورة.',
    voidTitle: 'إلغاء سند القبض {{number}}',
    voidBody:
      'يُرحِّل الإلغاء قيداً عكسياً ويعيد فتح الفواتير التي سدّدها سند القبض هذا. لا يمكن التراجع عن ذلك.',
  },
  statementNew: {
    title: 'إنشاء كشوف الحساب',
    enterDate: 'أدخل تاريخ الكشف.',
    tickCustomer: 'حدّد عميلاً واحداً على الأقل.',
    startAfterDate: 'يجب أن يكون تاريخ البداية في تاريخ الكشف أو قبله.',
    sent_zero: 'لم يُرسَل أي كشف حساب.',
    sent_one: 'أُرسل كشف حساب واحد.',
    sent_two: 'أُرسل كشفا حساب.',
    sent_few: 'أُرسلت {{count}} كشوف حساب.',
    sent_many: 'أُرسل {{count}} كشفَ حساب.',
    sent_other: 'أُرسل {{count}} كشف حساب.',
    sentSkipped_zero: 'لم يُرسَل أي كشف حساب، وتُخطّي {{skipped}}.',
    sentSkipped_one: 'أُرسل كشف حساب واحد، وتُخطّي {{skipped}}.',
    sentSkipped_two: 'أُرسل كشفا حساب، وتُخطّي {{skipped}}.',
    sentSkipped_few: 'أُرسلت {{count}} كشوف حساب، وتُخطّي {{skipped}}.',
    sentSkipped_many: 'أُرسل {{count}} كشفَ حساب، وتُخطّي {{skipped}}.',
    sentSkipped_other: 'أُرسل {{count}} كشف حساب، وتُخطّي {{skipped}}.',
    printPreview: 'طباعة أو معاينة',
    sending: 'جارٍ الإرسال…',
    saveAndSend: 'حفظ وإرسال',
    sentTo: 'أُرسل إلى {{list}}.',
    sentToRecorded: 'أُرسل إلى {{list}}. يحتفظ هذا الخادم بها في الرسائل المرسلة ولا يسلّمها.',
    recipient: '{{name}} ({{email}})',
    listSeparator: '، ',
    statementType: 'نوع الكشف',
    types: {
      balance_forward: 'رصيد مُرحَّل',
      open_item: 'البنود المفتوحة (الفواتير التي لا تزال مفتوحة)',
      transaction: 'كشف المعاملات',
    },
    statementDate: 'تاريخ الكشف',
    startDate: 'تاريخ البداية',
    endDate: 'تاريخ النهاية',
    customers: 'العملاء',
    withBalance: 'لديهم رصيد',
    withOverdue: 'لديهم رصيد متأخر',
    allCustomers: 'كل العملاء',
    explainBalanceForward:
      'الرصيد في {{from}}، ثم كل فاتورة ودفعة وإشعار دائن حتى {{to}} مع رصيد جارٍ.',
    explainOpenItem: 'الفواتير التي لا تزال مفتوحة في {{to}}، مع الأرصدة الدائنة غير المستخدمة.',
    explainTransaction: 'كل المعاملات من {{from}} إلى {{to}}.',
    noCustomers: 'لا يوجد عملاء لعرضهم',
    noCustomersHint: 'اختر «كل العملاء» لعرض جميع العملاء.',
    selectAll: 'تحديد كل العملاء',
    colCustomer: 'العميل',
    colEmail: 'البريد الإلكتروني',
    colOverdue: 'المتأخر',
    colBalance: 'الرصيد',
    include: 'تضمين {{name}}',
    noEmail: 'لا يوجد بريد إلكتروني: اطبع الكشف بدلاً من ذلك',
    selected_zero: 'لم يُحدَّد أي عميل',
    selected_one: 'عميل واحد محدد',
    selected_two: 'عميلان محددان',
    selected_few: '{{count}} عملاء محددون',
    selected_many: '{{count}} عميلاً محدداً',
    selected_other: '{{count}} عميل محدد',
    total: 'الإجمالي {{amount}}',
  },
  sendDialog: {
    title: 'إرسال {{what}}',
    separate: 'افصل بين العناوين المتعددة بفواصل.',
    separateRecorded:
      'افصل بين العناوين المتعددة بفواصل. يسجّل هذا الخادم الرسالة في الرسائل المرسلة بدلاً من تسليمها.',
    to: 'إلى *',
    cc: 'نسخة إلى',
    language: 'اللغة',
    languageHint: 'لغة الرسالة والمستند المرفق.',
    arabic: 'العربية',
    english: 'الإنجليزية',
    arabicUnavailable:
      'يستلم هذا العميل المستندات بالعربية، لكن هذا الخادم يطبع بالإنجليزية فقط حالياً.',
    subject: 'الموضوع *',
    message: 'الرسالة',
    sending: 'جارٍ الإرسال…',
    send: 'إرسال',
    failed: 'تعذّر تسليم {{what}}: {{error}}.',
    unknownError: 'خطأ غير معروف',
    recorded: 'سُجّل {{what}} لـ {{to}}. هذا الخادم لا يسلّم البريد الإلكتروني.',
    sent: 'أُرسل {{what}} إلى {{to}}.',
  },
};
